import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Seo from "@/components/feature/Seo";
import Breadcrumb from "@/components/feature/Breadcrumb";
import HotTicker from "@/components/feature/HotTicker";
import SentimentCard from "@/pages/event/components/SentimentCard";
import CommentSection from "@/pages/event/components/CommentSection";
import { getEventById, guEvents } from "@/mocks/events";

const formatHeat = (value: number) =>
  value >= 10000 ? `${(value / 10000).toFixed(1)}万` : `${value}`;

export default function EventDetail() {
  const { id = "" } = useParams();
  const event = getEventById(id);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [id]);

  if (!event) {
    return (
      <Layout>
        <Seo
          title="专题不存在 - 瓜田观察"
          description="你访问的吃瓜专题可能已被下架或链接有误，返回首页继续浏览最近的网红吃瓜事件。"
          keywords="网红吃瓜,专题不存在,瓜田观察"
          path="/event"
        />
        <div className="mx-auto flex w-full max-w-2xl flex-col items-center px-4 py-24 text-center md:px-8">
          <i className="ri-emotion-sad-line text-5xl text-foreground-400" />
          <h1 className="mt-5 font-heading text-2xl font-black text-foreground-950">
            这口瓜找不到了
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-foreground-600">
            这个专题可能已经被下架，或者链接有点问题。回到专题列表，还有一大堆瓜等着你。
          </p>
          <Link
            to="/topics"
            className="mt-6 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
          >
            返回热门专题
          </Link>
        </div>
      </Layout>
    );
  }

  const related = event.related
    .map((rid) => getEventById(rid))
    .filter((item): item is NonNullable<typeof item> => Boolean(item));
  const fallbackRelated = guEvents.filter((e) => e.id !== event.id && !event.related.includes(e.id));
  const relatedList = [...related, ...fallbackRelated].slice(0, 3);

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: event.title,
    description: event.summary,
    image: event.cover,
    datePublished: event.publishedAt,
    dateModified: event.publishedAt,
    inLanguage: "zh-CN",
    articleSection: event.categoryLabel,
    keywords: event.tags.join(","),
    author: { "@type": "Organization", name: "瓜田观察" },
    publisher: { "@type": "Organization", name: "瓜田观察" },
    mainEntityOfPage: `https://2ndhair.com.cn/event/${event.id}`,
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://2ndhair.com.cn/" },
      { "@type": "ListItem", position: 2, name: "热门专题", item: "https://2ndhair.com.cn/topics" },
      {
        "@type": "ListItem",
        position: 3,
        name: event.title,
        item: `https://2ndhair.com.cn/event/${event.id}`,
      },
    ],
  };

  return (
    <Layout>
      <Seo
        title={`${event.title} | 瓜田观察`}
        description={event.summary}
        keywords={`${event.tags.join(",")},网红吃瓜,评论区风向`}
        path={`/event/${event.id}`}
        ogType="article"
        jsonLd={[articleJsonLd, breadcrumbJsonLd]}
      />
      <HotTicker />

      <div className="mx-auto w-full px-4 py-6 md:px-8 md:py-8">
        <Breadcrumb items={[{ label: "热门专题", to: "/topics" }, { label: event.categoryLabel }]} />
      </div>

      <article className="mx-auto w-full px-4 pb-14 md:px-8">
        <header className="overflow-hidden rounded-lg border border-background-200 bg-background-100">
          <div className="relative h-[240px] w-full md:h-[420px]">
            <img
              src={event.cover}
              alt={`${event.title} - 网红吃瓜事件配图`}
              title={`${event.title} 评论区风向`}
              className="h-full w-full object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-foreground-950/25 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-5 md:p-8">
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <Link
                  to={`/topics?category=${event.category}`}
                  className="cursor-pointer rounded-full bg-primary-500 px-3 py-1 text-xs font-bold text-background-50"
                >
                  {event.categoryLabel}
                </Link>
                <span className="flex items-center gap-1 rounded-full bg-background-50/90 px-3 py-1 text-xs font-semibold text-foreground-800">
                  <i className="ri-fire-line text-sm text-primary-600" />
                  热度 {formatHeat(event.heat)}
                </span>
                <span className="flex items-center gap-1 rounded-full bg-background-50/90 px-3 py-1 text-xs font-semibold text-foreground-800">
                  <i className="ri-calendar-line text-sm" />
                  {event.publishedAt}
                </span>
              </div>
              <h1 className="max-w-4xl font-heading text-2xl font-black leading-snug text-background-50 md:text-4xl">
                {event.title}
              </h1>
            </div>
          </div>
        </header>

        <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-[1.7fr_1fr]">
          <div className="min-w-0">
            <p className="border-l-4 border-accent-500 bg-accent-50 px-5 py-4 font-heading text-base leading-relaxed text-foreground-900">
              {event.summary}
            </p>

            <div className="mt-7 flex flex-col gap-5">
              {event.content.map((paragraph, index) => (
                <p key={index} className="text-[15px] leading-8 text-foreground-800">
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap gap-2">
              {event.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/topics?q=${encodeURIComponent(tag)}`}
                  className="cursor-pointer rounded-full bg-secondary-100 px-3 py-1.5 text-xs font-medium text-secondary-900 transition-colors hover:bg-secondary-200"
                >
                  #{tag}
                </Link>
              ))}
            </div>

            <div className="mt-10">
              <CommentSection
                highlight={event.highlight}
                comments={event.comments}
                total={event.commentCount}
              />
            </div>
          </div>

          <aside className="flex flex-col gap-5 lg:sticky lg:top-24 lg:h-fit">
            <SentimentCard sentiment={event.sentiment} commentCount={event.commentCount} />

            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-timeline-view text-lg text-secondary-600" />
                事件速览
              </h3>
              <ul className="mt-4 flex flex-col gap-3 text-sm">
                <li className="flex items-center justify-between border-b border-background-200 pb-3">
                  <span className="text-foreground-600">话题热度</span>
                  <span className="font-bold text-foreground-950">{formatHeat(event.heat)}</span>
                </li>
                <li className="flex items-center justify-between border-b border-background-200 pb-3">
                  <span className="text-foreground-600">评论总数</span>
                  <span className="font-bold text-foreground-950">
                    {event.commentCount.toLocaleString("zh-CN")}
                  </span>
                </li>
                <li className="flex items-center justify-between border-b border-background-200 pb-3">
                  <span className="text-foreground-600">所属分类</span>
                  <span className="font-bold text-foreground-950">{event.categoryLabel}</span>
                </li>
                <li className="flex items-center justify-between">
                  <span className="text-foreground-600">首发日期</span>
                  <span className="font-bold text-foreground-950">{event.publishedAt}</span>
                </li>
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-100 p-5">
              <h3 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-links-line text-lg text-primary-600" />
                相关推荐
              </h3>
              <ul className="mt-4 flex flex-col gap-3">
                {relatedList.map((item) => (
                  <li key={item.id}>
                    <Link
                      to={`/event/${item.id}`}
                      className="group flex cursor-pointer gap-3"
                    >
                      <span className="h-14 w-20 shrink-0 overflow-hidden rounded-md">
                        <img
                          src={item.cover}
                          alt={`${item.title} - 相关吃瓜专题配图`}
                          title={item.title}
                          className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
                        />
                      </span>
                      <span className="min-w-0">
                        <span className="line-clamp-2 text-sm font-medium leading-snug text-foreground-900 transition-colors group-hover:text-primary-700">
                          {item.title}
                        </span>
                        <span className="mt-1 block text-xs text-foreground-500">
                          {item.categoryLabel}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <Link
              to="/#subscribe"
              className="flex cursor-pointer flex-col gap-2 rounded-lg bg-foreground-950 p-5 text-background-50 transition-colors hover:bg-foreground-900"
            >
              <span className="flex items-center gap-2 font-heading text-base font-bold">
                <i className="ri-notification-3-line text-lg text-accent-400" />
                订阅每日吃瓜速报
              </span>
              <span className="text-xs leading-relaxed text-background-300">
                每天一场精选瓜 + 三条高赞神评，三分钟看完评论区风向。
              </span>
            </Link>
          </aside>
        </div>
      </article>
    </Layout>
  );
}